import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const BulkActionsModal = ({ isOpen, onClose, onAction, selectedCount, loading }) => {
  const [selectedAction, setSelectedAction] = useState('');
  const [actionData, setActionData] = useState({});

  const actions = [
    {
      value: 'status_change',
      label: 'Change Status',
      description: 'Activate or deactivate selected users',
      icon: 'ToggleLeft',
      color: 'text-warning'
    },
    {
      value: 'delete',
      label: 'Delete Users',
      description: 'Permanently remove selected users',
      icon: 'Trash2',
      color: 'text-error'
    }
  ];

  const inactiveRemarks = [
    'Account suspended by admin',
    'User requested deactivation',
    'Policy violation',
    'Security concern',
    'Duplicate account',
    'Contract terminated',
    'User no longer active',
    'Data integrity issue',
    'Compliance requirement',
    'Other reason'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onAction(selectedAction, actionData);
  };

  const handleClose = () => {
    setSelectedAction('');
    setActionData({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-secondary-900 bg-opacity-50 flex items-center justify-center z-1001 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">Bulk Actions</h2>
            <p className="text-sm text-secondary-600 mt-1">
              Perform actions on {selectedCount} selected user{selectedCount === 1 ? '' : 's'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-md transition-colors duration-150"
            disabled={loading}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            {/* Action Selection */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                Select Action
              </label>
              <div className="space-y-2">
                {actions.map(action => (
                  <label key={action.value} className="flex items-center p-3 border border-secondary-200 rounded-lg hover:bg-secondary-50 cursor-pointer">
                    <input
                      type="radio"
                      name="action"
                      value={action.value}
                      checked={selectedAction === action.value}
                      onChange={(e) => setSelectedAction(e.target.value)}
                      className="w-4 h-4 text-primary border-secondary-300 focus:ring-primary-500"
                      disabled={loading}
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-center">
                        <Icon name={action.icon} size={16} className={`mr-2 ${action.color}`} />
                        <span className="text-sm font-medium text-text-primary">{action.label}</span>
                      </div>
                      <p className="text-xs text-secondary-600 mt-1">{action.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Status Change Options */}
            {selectedAction === 'status_change' && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-text-primary mb-2">
                    New Status
                  </label>
                  <select
                    id="status"
                    value={actionData.status || ''}
                    onChange={(e) => setActionData(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                    disabled={loading}
                  >
                    <option value="">Select status...</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                {actionData.status === 'inactive' && (
                  <div>
                    <label htmlFor="remarks" className="block text-sm font-medium text-text-primary mb-2">
                      Reason for Deactivation *
                    </label>
                    <select
                      id="remarks"
                      value={actionData.remarks || ''}
                      onChange={(e) => setActionData(prev => ({ ...prev, remarks: e.target.value }))}
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                      disabled={loading}
                    >
                      <option value="">Select reason...</option>
                      {inactiveRemarks.map((remark, index) => (
                        <option key={index} value={remark}>
                          {remark}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Delete Confirmation */}
            {selectedAction === 'delete' && (
              <div className="p-4 bg-error-50 border border-error-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <Icon name="AlertTriangle" size={16} className="text-error-600 mt-0.5" />
                  <div className="text-sm text-error-800">
                    <strong>Warning:</strong> This action cannot be undone. All selected users will be permanently deleted from the system.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 bg-secondary-50 border-t border-secondary-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedAction}
              className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-lg focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                selectedAction === 'delete' 
                  ? 'bg-error hover:bg-error-700 focus:ring-error-500' 
                  : 'bg-primary hover:bg-primary-700 focus:ring-primary-500'
              }`}
            >
              {loading ? (
                <>
                  <Icon name="Loader2" size={16} className="animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <Icon name="Check" size={16} className="mr-2" />
                  Apply to {selectedCount} User{selectedCount === 1 ? '' : 's'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkActionsModal; 